import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useVesselWarrantySummary } from '@/features/warranty/queries';
import { daysFromToday } from '@/features/warranty/format';
import { cn } from '@/lib/utils';

interface VesselWarrantyCardProps {
  vesselId: string;
}

/**
 * Vessel-level warranty card. Shows the fleet-wide end date with a
 * countdown and the breakdown by source (inherited / standalone /
 * extended). Clickable equipment chips for the "expiring within
 * 90 days" subset are deferred to a polish lot — the data is
 * already there, just no router target wired yet.
 */
export function VesselWarrantyCard({ vesselId }: VesselWarrantyCardProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useVesselWarrantySummary(vesselId);
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck aria-hidden className="h-4 w-4" />
            {t('warranty.summary.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const endDate = data.vessel.warrantyEndDate;
  const daysLeft = endDate ? daysFromToday(endDate) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck aria-hidden className="h-4 w-4" />
          {t('warranty.summary.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {endDate ? (
          <div className="flex flex-col gap-1">
            <span className="text-sm">
              {t('warranty.endDate')}:{' '}
              <span className="font-medium">
                {new Date(endDate).toLocaleDateString()}
              </span>
            </span>
            <span
              className={cn(
                'text-xs',
                daysLeft !== null && daysLeft < 0
                  ? 'text-red-700'
                  : daysLeft !== null && daysLeft < 90
                    ? 'text-amber-700'
                    : 'text-emerald-700',
              )}
            >
              {daysLeft !== null && daysLeft >= 0
                ? t('warranty.expiresIn', { count: daysLeft })
                : daysLeft !== null
                  ? t('warranty.expired', { count: Math.abs(daysLeft) })
                  : null}
            </span>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('warranty.summary.noWarranty')}
          </p>
        )}

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center rounded-md bg-blue-100 px-2 py-0.5 font-medium text-blue-700">
            {t('warranty.summary.inherited', {
              count: data.equipmentBreakdown.inherited,
            })}
          </span>
          <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 font-medium text-amber-700">
            {t('warranty.summary.original', {
              count: data.equipmentBreakdown.original,
            })}
          </span>
          <span className="inline-flex items-center rounded-md bg-emerald-100 px-2 py-0.5 font-medium text-emerald-700">
            {t('warranty.summary.extended', {
              count: data.equipmentBreakdown.extendedByRepair,
            })}
          </span>
        </div>

        {data.expiringWithin90Days.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t('warranty.summary.expiringSoon', {
              count: data.expiringWithin90Days.length,
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
